import { inferConfigSettingType } from './inferConfigSettingType';
import { minorUnitsFromAmount, normalizeAmountString } from './moneyAmount';
import type { AllowedOption, ConfigSettingLike, ConfigValue, MoneyJson } from './types';

export const dataTypeOf = (s: ConfigSettingLike): string =>
    s.configOption?.data_type ?? inferConfigSettingType(s.value);

const currencySymbol = (currency: string): string => {
    if (currency === 'GBP') return '£';
    if (currency === 'EUR') return '€';
    if (currency === 'USD') return '$';
    return currency;
};

export const humanizeKey = (key: string): string =>
    key.replace(/[_-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Multiselect values are held in the edit input as a comma-joined string (the checkbox
// group serialises the checked keys), then split back to a string[] on save. Allowed keys
// are snake_case with no commas, so comma is a safe delimiter.
export const splitCsv = (raw: string): string[] =>
    raw.split(',').map((s) => s.trim()).filter(Boolean);

// Selectable {value, label} options a `multiselect` (or future single-select `dropdown`)
// option offers, from its `validation_rules.allowed`. Labels are MANDATORY catalog data --
// there is no humanizeKey() fallback per-option; an option with a malformed allowed entry
// (missing `value` or `label`) is dropped rather than rendered with a guessed name.
export const extractAllowedOptions = (
    option: ConfigSettingLike['configOption'],
): AllowedOption[] => {
    const allowed = option?.validation_rules?.allowed;
    if (!Array.isArray(allowed)) return [];
    return allowed.filter(
        (v): v is AllowedOption =>
            typeof v === 'object' && v !== null &&
            typeof (v as AllowedOption).value === 'string' &&
            typeof (v as AllowedOption).label === 'string',
    );
};

export function formatConfigValue(s: ConfigSettingLike): string {
    const dataType = dataTypeOf(s);
    if (dataType === 'boolean') {
        return s.value === true || s.value === 'true' ? 'Yes' : 'No';
    }
    if (dataType === 'money') {
        const m = s.value as MoneyJson;
        return `${currencySymbol(m.currency)}${m.amount}`;
    }
    if (Array.isArray(s.value)) {
        const allowed = extractAllowedOptions(s.configOption);
        const labelOf = (v: unknown): string =>
            allowed.find((o) => o.value === v)?.label ?? humanizeKey(String(v));
        return s.value.map(labelOf).join(', ');
    }
    return String(s.value);
}

export function seedConfigDraft(s: ConfigSettingLike): string {
    const dataType = dataTypeOf(s);
    if (dataType === 'money') {
        return (s.value as MoneyJson).amount;
    }
    if (dataType === 'boolean') {
        return s.value === true || s.value === 'true' ? 'true' : 'false';
    }
    if (Array.isArray(s.value)) {
        return s.value.join(',');
    }
    return String(s.value);
}

export type CoerceResult = { ok: true; value: ConfigValue } | { ok: false; error: string };

export function coerceConfigValue(s: ConfigSettingLike, raw: string): CoerceResult {
    const dataType = dataTypeOf(s);

    if (dataType === 'boolean') {
        return { ok: true, value: raw === 'true' };
    }
    if (dataType === 'multiselect') {
        const selected = splitCsv(raw);
        if (selected.length === 0) {
            return { ok: false, error: 'Select at least one option' };
        }
        return { ok: true, value: selected };
    }
    if (dataType === 'integer') {
        // Mirror the add-form: a bare `Number.isFinite(parseInt(...))` check accepts
        // `1.5` (-> 1) and `5abc` (-> 5). Require a strict whole-number string.
        const trimmed = raw.trim();
        if (!/^-?\d+$/.test(trimmed)) {
            return { ok: false, error: 'Enter a whole number' };
        }
        return { ok: true, value: parseInt(trimmed, 10) };
    }
    if (dataType === 'decimal') {
        // Mirror the add-form: validate the raw string with normalizeAmountString
        // (rejects `5abc` and other partially-numeric input) before parsing.
        if (normalizeAmountString(raw) === null) {
            return { ok: false, error: 'Enter a numeric amount' };
        }
        return { ok: true, value: parseFloat(raw) };
    }
    if (dataType === 'money') {
        const existing = s.value as MoneyJson;
        const amount = normalizeAmountString(raw);
        if (amount === null) {
            return { ok: false, error: 'Enter a numeric amount' };
        }
        const minor = minorUnitsFromAmount(amount);
        if (minor === null) {
            return { ok: false, error: 'Enter a numeric amount' };
        }
        // minor computed via decimal-safe string arithmetic — never parseFloat * 100.
        return { ok: true, value: { amount, currency: existing.currency, minor } };
    }
    if (raw.trim() === '') {
        // Backend rejects an empty `value` (required). Don't submit.
        return { ok: false, error: 'Value is required' };
    }
    return { ok: true, value: raw };
}
